import React, { useCallback } from "react";
import { toast } from "react-toastify";
import SpeechEvaluation from "../quiz/SpeechEvaluation";
import { getSpeechConfig, textToSpeech } from "../../store/quiz/quizBaseApi";
import { addSpeechEvaluationHistory } from "../../store/quiz/quizBaseApi";
import { useStudy } from "../../pages/study/hooks";
import { Box, Typography, Button } from "@mui/material";
import { useQuiz } from "../../pages/quiz/hooks";
import studentSvg from "../../assets/svgs/speechEvaluationIcons/student.svg";
import arrowSvg from "../../assets/svgs/arrow.svg";

const CONTENT_SEPARATOR = ";";
const NETWORK_ERR_THRESHOLD = 5;
const MAX_RETRIES = 3;
const MIN_PASSING_SCORE = 40;

export default function AsyncSpeechEvaluation({
  onComplete,
  importantWords,
  onFailureThresholdReached,
  dictionary = [],
  onStart,
  question,
  sectionId,
  chapterId,
  courseId,
  saveHistory = true,
  skipFailures = false,
  showText,
  sampleRate,
  channelCount,
}) {
  const [textArray, setTextArray] = React.useState([]);
  const [index, setIndex] = React.useState(0);
  const [errored, setErrored] = React.useState(false);
  const [start, setStart] = React.useState(false);
  const [networkFailures, setNetworkFailures] = React.useState(0);
  // Empty string, never `false`: this value is handed straight down as
  // question.text, and a boolean there made every `.match(...)`/`.split(...)`
  // call in SpeechEvaluation throw ("t.text.match is not a function").
  const [text, setText] = React.useState("");
  const [completed, setCompleted] = React.useState(false);

  // Prefetched TTS audio URLs for the paragraph sentences.
  // Centralize TTS calls here and pass prefetched URL down to SpeechEvaluation.
  const [ttsUrls, setTtsUrls] = React.useState([]);

  // Prefetch TTS for all sentences when evaluation starts (start === true).
  React.useEffect(() => {
    let mounted = true;
    if (!start || !textArray || !textArray.length) return;

    (async () => {
      try {
        const profile = JSON.parse(localStorage.getItem("profile")) || {};
        const conf = await getSpeechConfig(
          String(profile.geographicalType).toLowerCase(),
          profile.learningGeneration,
        );

        const urls = await Promise.all(
          textArray.map(async (txt) => {
            try {
              const res = await textToSpeech({
                text: txt,
                ...conf,
                accent: conf.accent,
              });
              if (res && res.response && res.response.audio)
                return res.response.audio;
            } catch (e) {
              // swallow individual errors so other sentences can still load
            }
            return null;
          }),
        );

        if (mounted) setTtsUrls(urls);
      } catch (e) {
        // top-level errors ignored; individual failures are tolerated
        console.error("Error prefetching TTS:", e);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [start, textArray]);

  const {
    profile,
    scores,
    setScores,
    clearScores,
    setActiveSentence,
    clearWordsHistory,
  } = useStudy();
  const { setCurrentSpeechSentance } = useQuiz();

  const evaluations = React.useRef({});
  const currentText = React.useRef("");
  const failed = React.useRef(false);
  const speechTemp = React.useRef([]);

  const onSpeechEvaluationCompletion = useCallback(
    async (outcome, audio, idx, failures) => {
      let score = 0;
      let { status } = outcome || {};
      let error = false;

      if (outcome && outcome.hasOwnProperty("overallScore")) {
        score = outcome.overallScore;
        saveHistory &&
          addSpeechEvaluationHistory({
            params: { courseId },
            body: {
              readingScore: outcome.overallScore,
              assigneeId: profile.id,
              outcome: JSON.stringify(outcome),
              sectionId,
              chapterId,
              text: textArray[index],
            },
            audio,
          })
            .then((res) => {})
            .catch((e) =>
              toast.error(
                "Error while posting the speech evaluation history: " +
                  e.message,
              ),
            );

        failed.current = false;
      } else if (outcome && status == "failed") {
        error = !skipFailures;
      }
      let spList = speechTemp.current;
      let audioDetails = {
        text: textArray[index],
        audioUrl: audio,
        outcome,
        status,
        score,
        // Number of incorrect attempts before this read was accepted. `failures`
        // is the child's per-sentence counter (reset on each new sentence); it was
        // already passed up here and previously discarded. 0 = first-try pass.
        retryCount: Number.isFinite(failures) ? failures : 0,
      };
      spList.push(audioDetails);
      evaluations.current = {
        ...evaluations.current,
        [idx]: {
          text: textArray[idx],
          evaluated: true,
          evaluating: false,
          error,
          score,
        },
      };

      let averageScore = evaluateAvgScore();
      // Complete when the current sentence is the last index OR every sentence has
      // been evaluated. The all-evaluated check is a safety net: if the last read
      // to resolve isn't index length-1 (out-of-order retries / a stale index on
      // the completing call), the plain index check would silently never fire and
      // the Continue button would never appear.
      const allEvaluated =
        Object.keys(evaluations.current).length > 0 &&
        Object.values(evaluations.current).every((e) => e && e.evaluated);
      if (
        allEvaluated ||
        Number(idx) >= Object.keys(evaluations.current).length - 1
      ) {
        setCompleted(true);

        if (onComplete && typeof onComplete === "function") {
          onComplete(
            evaluations.current,
            speechTemp.current,
            averageScore.score,
          );
        }
      }

      checkEvaluation();
      setScores({ index: idx, score, text: textArray[idx] });
    },
    [
      saveHistory,
      courseId,
      profile,
      sectionId,
      chapterId,
      textArray,
      index,
      skipFailures,
      evaluateAvgScore,
      onComplete,
      checkEvaluation,
      setCompleted,
      setScores,
      toast,
    ],
  );

  /**
   * Checks if the average score is less than or equal to the MIN_PASSING_SCORE
   */
  const evaluateAvgScore = () => {
    /**
     * @var eachEvalmark
     * @description Each evaluation is scored out of 100.
     * Hence, we can consider that for each evaluation the full mark is 100
     */
    let eachEvalmark = 100;
    let evals = Object.values(evaluations.current);
    let totalScore = evals.reduce((prev, curr) => prev + Number(curr.score), 0);
    let avg = (totalScore / (evals.length * eachEvalmark)) * 100;

    return {
      pass: avg <= MIN_PASSING_SCORE,
      score: avg,
    };
  };

  const handleStart = () => {
    if (onStart && typeof onStart === "function") {
      setStart(true);
      onStart();
    }
  };

  const checkEvaluation = () => {
    if (networkFailures >= NETWORK_ERR_THRESHOLD) return;
    let failedElem = null,
      index = null;

    for (const key in evaluations.current) {
      const element = evaluations.current[key];
      if (element.error || element.score == null) {
        failedElem = element;
        index = key;
        break;
      }
    }

    if (failedElem && failedElem["text"] != text) {
      const numIndex = Number(index);
      setIndex(numIndex);
      setCurrentSpeechSentance(numIndex);
      setActiveSentence(numIndex);
      setText(failedElem["text"]);
      failed.current = true;
    } else {
      failed.current = false;
      // onEvaluationStarted();
    }
  };

  const onEvaluationStarted = useCallback(
    (ind) => {
      evaluations.current = {
        ...evaluations.current,
        [ind]: { ...evaluations.current[ind], evaluating: true },
      };
    },
    [evaluations],
  );

  const handleFailureThresholdReached = useCallback(
    async ({ meta, audio, index: childIndex } = {}) => {
      // Centralized handler: generate TTS for the failed sentence index once, update local cache,
      // and then forward to any external onFailureThresholdReached callback.
      try {
        // Ensure a valid child index
        const idx = typeof childIndex === "number" ? childIndex : index;
        const txt = textArray[idx];
        // If no text available, forward with null audio
        if (!txt) {
          if (
            onFailureThresholdReached &&
            typeof onFailureThresholdReached === "function"
          ) {
            onFailureThresholdReached({ audio: null, meta });
          }
          return;
        }
        // First, if parent has already prefetched TTS for this index, reuse it
        const existing = ttsUrls && ttsUrls[idx];
        if (existing) {
          if (
            onFailureThresholdReached &&
            typeof onFailureThresholdReached === "function"
          ) {
            console.log("found existing url: ", existing);
            onFailureThresholdReached({ audio: existing, meta });
          }
          return;
        }
        // Build speech config and request TTS once
        const profileLocal = JSON.parse(localStorage.getItem("profile")) || {};
        const conf = await getSpeechConfig(
          String(profileLocal.geographicalType).toLowerCase(),
          profileLocal.learningGeneration,
        );
        const res = await textToSpeech({
          text: txt,
          ...conf,
          accent: conf.accent,
        });
        const sentanceAudioUrl = res?.response?.audio || null;
        // store into parent-level cache so child receives it via prefetchedSentanceAudio prop
        setTtsUrls((prev) => {
          const next = Array.isArray(prev) ? [...prev] : [];
          next[idx] = sentanceAudioUrl;
          return next;
        });
        // Call external callback (if provided) with the generated audio and meta
        if (
          onFailureThresholdReached &&
          typeof onFailureThresholdReached === "function"
        ) {
          onFailureThresholdReached({ audio: sentanceAudioUrl, meta });
        }
      } catch (e) {
        console.error("Error generating TTS in parent handler:", e);
        if (
          onFailureThresholdReached &&
          typeof onFailureThresholdReached === "function"
        ) {
          // still forward so external handlers can react to failure
          onFailureThresholdReached({ audio: null, meta });
        }
      }
    },
    [
      index,
      textArray,
      ttsUrls,
      setTtsUrls,
      onFailureThresholdReached,
      getSpeechConfig,
      textToSpeech,
    ],
  );

  React.useEffect(() => {
    // Accepts both shapes callers pass: [{ text, image }] (markup chapters) and
    // ["raw sentence"] (contentURL chapters). Anything that isn't a non-blank
    // string is dropped here rather than reaching SpeechEvaluation, where it
    // used to throw on `e.length` or surface as a non-string question.text.
    const sentences = (Array.isArray(question) ? question : [])
      .map((q) => (typeof q === "string" ? q : q?.text))
      .filter((t) => typeof t === "string" && t.trim());

    let _evals = {};

    sentences.forEach((e, i) => {
      _evals[i] = {
        text: e,
        evaluated: false,
        error: null,
        score: null,
        evaluating: false,
      };
    });

    // Reset (rather than early-return) on an empty list, so a chapter with no
    // readable content can't inherit the previous chapter's sentences.
    setTextArray(sentences);
    setText(sentences[0] || "");
    setCurrentSpeechSentance(0);
    setActiveSentence(0);
    setCompleted(false);
    clearScores();
    clearWordsHistory();
    setStart(false);
    setIndex(0);

    evaluations.current = _evals;
  }, [question]);

  return (
    <React.Fragment>
      {!textArray.length ? // No sentence to read: render nothing rather than "Click to Read",
      // because starting a recording here would auto-stop instantly and
      // submit nothing. Deliberately NOT an error message — callers fill
      // `question` from async fetches, so an empty list also means "still
      // loading". The parent that knows its data has settled
      // (StoryWithKeywords) is the one that explains this to the student.
      null : start && networkFailures < NETWORK_ERR_THRESHOLD ? (
        <div>
          <SpeechEvaluation
            showText={showText}
            onEvaluationStarted={onEvaluationStarted}
            onComplete={(r, a, f) =>
              onSpeechEvaluationCompletion(r, a, index, f)
            }
            onFailureThresholdReached={handleFailureThresholdReached}
            // onNetworkFailure={() => setNetworkFailures(prev => prev + 1)}
            disabled={completed}
            question={{ text, index }}
            passingScore={MIN_PASSING_SCORE}
            importantWords={importantWords}
            error={errored}
            skipFailures={skipFailures}
            paragraph={textArray}
            maxRetries={MAX_RETRIES}
            dictionary={dictionary}
            sampleRate={sampleRate}
            channelCount={channelCount}
            // Provide prefetched sentence audio (may be null if not ready)
            prefetchedSentanceAudio={
              ttsUrls && ttsUrls[index] ? ttsUrls[index] : null
            }
          />
        </div>
      ) : networkFailures >= NETWORK_ERR_THRESHOLD ? (
        <div style={styles.btnContainer}>
          Too many network failures, aborting...
        </div>
      ) : (
        <Box sx={{ position: "relative", paddingBottom: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <img style={{ width: "35%" }} src={studentSvg} alt="" />
          </Box>
          <Box
            sx={{
              background:
                "linear-gradient(0deg, #CCCCCC -2.68%, #E6E6E6 31.68%, #F8F8F8 64.23%, #FFFFFF 87.74%)",
              left: "50%",
              borderRadius: "50px",
              padding: "5px 5px",
              transform: "translateX(-50%)",
              position: "absolute",
              bottom: "-10px",
              width: { xs: "60%", sm: "auto" },
            }}
          >
            <Button
              sx={{
                background:
                  "linear-gradient(to top, #01afff, #23b5ff, #36bbff, #46c1ff, #54c7ff, #5ecafe, #67cefe, #70d1fd, #78d2fc, #7fd4fa, #85d5f9, #8cd6f8)",
                color: "#FFFFFF",
                width: "100%",
                borderRadius: "50px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: { xs: "10px 10px", sm: "10px 20px" },
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.2)",
                "&:hover": {
                  background:
                    "linear-gradient(to top, #0156ff, #006bff, #007dff, #008eff, #009dff, #00a4ff, #00abff, #0fb2ff, #18b5ff, #1fb7ff, #26baff, #2cbcff)",
                  boxShadow: "0 6px 10px rgba(0, 0, 0, 0.3)",
                },
              }}
              onClick={handleStart}
            >
              <Typography
                sx={{
                  fontWeight: "700",
                  fontFamily: '"Quicksand", sans-serif',
                  fontSize: {
                    xs: "1em",
                    sm: "1em",
                    md: "1.4em",
                    lg: "1em",
                    xl: "1.8em",
                  },
                  zIndex: 2,
                }}
              >
                Click to Read
              </Typography>
              <img
                style={{
                  width: "15%",
                  maxWidth: "100%",
                  marginTop: "5px",
                  height: "auto",
                  zIndex: 2,
                }}
                src={arrowSvg}
                alt=""
              />
            </Button>
          </Box>
        </Box>
      )}
    </React.Fragment>
  );
}

const styles = {
  btnContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: "2rem",
    marginBottom: "2rem",
  },
  startBtn: {
    marginTop: "2rem",
    marginBottom: "2rem",
  },
  startBtnInner: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
};
